/**
 * @fileoverview CustomTour.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential {t('forms.and')} proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information {t('forms.and')} shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import SEO from '../components/ui/SEO'
import DynamicImage from '../components/ui/DynamicImage'
import { apiFetch } from '../utils/api'

function CustomTour() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: 'Mr',
    name: '',
    email: '',
    phoneCode: '+94',
    phone: '',
    country: 'United States',
    languages: [],
    arrivalDate: '',
    arrivalTime: '',
    departureDate: '',
    departureTime: '',
    travelers: {
      adults: 1,
      children: 0,
      seniors: 0,
      infants: 0
    },
    selectedCategories: [],
    selectedExperiences: [],
    tourTier: 'basic',
    specialNotes: ''
  });

  const categoriesList = ['Family', 'Adventure', 'Cultural', 'Nature', 'Beach', 'Wildlife', 'Wellness', 'Romantic'];
  const popularCountryCodes = [
    { code: '+94', name: 'LK' }, { code: '+1', name: 'US/CA' }, { code: '+44', name: 'UK' }, 
    { code: '+61', name: 'AU' }, { code: '+49', name: 'DE' }, { code: '+33', name: 'FR' },
    { code: '+81', name: 'JP' }, { code: '+86', name: 'CN' }, { code: '+91', name: 'IN' },
    { code: '+971', name: 'AE' }
  ];
  const popularCountries = [
    'United States', 'United Kingdom', 'Australia', 'Germany', 'France', 'Canada', 
    'Japan', 'China', 'India', 'United Arab Emirates', 'Sri Lanka'
  ];

  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successBooking, setSuccessBooking] = useState(null);
  const [activeStep, setActiveStep] = useState(1);
  const [passengerDetails, setPassengerDetails] = useState([]);
  const [skipPassengerDetails, setSkipPassengerDetails] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);

    apiFetch('/experiences')
      .then(res => {
        if (res && res.experiences && res.experiences.length > 0) {
          const formatted = res.experiences.map(exp => ({
            id: exp._id,
            name: exp.title,
            description: exp.description || '',
            categories: exp.categories || [],
            image: (exp.image && typeof exp.image === 'object' && (exp.image.l || exp.image.h)) 
              ? { l: exp.image.l || exp.image.h, h: exp.image.h || exp.image.l }
              : { l: exp.image || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800', h: exp.image || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800' }
          })).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
          setExperiences(formatted);
        } else {
          setExperiences([]);
        }
      })
      .catch(() => {
        setExperiences([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const getMinArrivalDate = () => {
      const d = new Date();
      d.setDate(d.getDate() + 20);
      return d.toISOString().split('T')[0];
  };

  const getMinDepartureDate = () => {
      if (formData.arrivalDate) {
          const d = new Date(formData.arrivalDate);
          d.setDate(d.getDate() + 1);
          return d.toISOString().split('T')[0];
      }
      return getMinArrivalDate();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLanguageChange = (lang) => {
    setFormData(prev => {
      const languages = prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang];
      return { ...prev, languages };
    });
  };

  const updateTraveler = (type, delta) => {
    setFormData(prev => ({
      ...prev,
      travelers: {
        ...prev.travelers,
        [type]: Math.max(0, prev.travelers[type] + delta)
      }
    }));
  };

  const toggleExperience = (expId) => {
    setFormData(prev => {
      const selectedExperiences = prev.selectedExperiences.includes(expId)
        ? prev.selectedExperiences.filter(id => id !== expId)
        : [...prev.selectedExperiences, expId];
      return { ...prev, selectedExperiences };
    });
  };

  const toggleCategory = (cat) => {
    setFormData(prev => {
      const selectedCategories = prev.selectedCategories.includes(cat)
        ? prev.selectedCategories.filter(c => c !== cat)
        : [...prev.selectedCategories, cat];
      return { ...prev, selectedCategories };
    });
  };

  const preparePassengerDetails = () => {
    const targetList = [];
    const adults = formData.travelers.adults || 0;
    const children = formData.travelers.children || 0;
    const seniors = formData.travelers.seniors || 0;
    const infants = formData.travelers.infants || 0;

    const findExisting = (ageType, indexInType) => {
      return passengerDetails.find(p => p.ageType === ageType && p.indexInType === indexInType);
    };

    for (let i = 0; i < adults; i++) {
      const existing = findExisting('adult', i);
      targetList.push({
        name: existing ? existing.name : '',
        passport: existing ? existing.passport : '',
        ageType: 'adult',
        indexInType: i,
        label: `Adult Guest ${i + 1}`
      });
    }
    for (let i = 0; i < children; i++) {
      const existing = findExisting('kid', i);
      targetList.push({
        name: existing ? existing.name : '',
        passport: existing ? existing.passport : '',
        ageType: 'kid',
        indexInType: i,
        label: `Child Guest ${i + 1}`
      });
    }
    for (let i = 0; i < seniors; i++) {
      const existing = findExisting('senior', i);
      targetList.push({
        name: existing ? existing.name : '',
        passport: existing ? existing.passport : '',
        ageType: 'senior',
        indexInType: i,
        label: `Senior Guest ${i + 1}`
      });
    }
    for (let i = 0; i < infants; i++) {
      const existing = findExisting('infant', i);
      targetList.push({
        name: existing ? existing.name : '',
        passport: existing ? existing.passport : '',
        ageType: 'infant',
        indexInType: i,
        label: `Infant Guest ${i + 1}`
      });
    }
    setPassengerDetails(targetList);
  };

  const validateStep = (step) => {
    if (step === 1) {
      if (!formData.name.trim()) { alert('Please enter your full name.'); return false; }
      if (!formData.email.trim() || !formData.email.includes('@')) { alert('Please enter a valid email address.'); return false; }
      if (!formData.phone.trim()) { alert('Please enter your phone number.'); return false; }
      if (!formData.country.trim()) { alert('Please enter your nationality.'); return false; }
    }
    if (step === 2) {
      if (!formData.arrivalDate) { alert('Please select arrival date.'); return false; }
      if (!formData.departureDate) { alert('Please select departure date.'); return false; }
      if (new Date(formData.departureDate) < new Date(formData.arrivalDate)) {
        alert('Departure date cannot be before arrival date.');
        return false;
      }
      const adults = formData.travelers.adults || 0;
      const children = formData.travelers.children || 0;
      const seniors = formData.travelers.seniors || 0;
      const infants = formData.travelers.infants || 0;
      if (adults + children + seniors + infants === 0) {
        alert('Please select at least 1 traveler.');
        return false;
      }
    }
    if (step === 4) {
      if (!skipPassengerDetails) {
        for (let i = 0; i < passengerDetails.length; i++) {
          const p = passengerDetails[i];
          if (!p.name.trim()) {
            alert(`Please enter the name for traveler #${i + 1} (${p.label}).`);
            return false;
          }
          if (!p.passport.trim()) {
            alert(`Please enter the passport number for traveler #${i + 1} (${p.label}).`);
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleStepClick = (stepId) => {
    if (stepId < activeStep) {
      setActiveStep(stepId);
    } else if (stepId > activeStep) {
      for (let s = activeStep; s < stepId; s++) {
        if (!validateStep(s)) return;
        if (s === 2) {
          preparePassengerDetails();
        }
      }
      setActiveStep(stepId);
    }
  };

  const handleNextStep = () => {
    if (!validateStep(activeStep)) return;
    if (activeStep === 2) {
      preparePassengerDetails();
    }
    setActiveStep(p => Math.min(5, p + 1));
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!agreedToTerms) {
      alert("Please agree to the Terms of Use {t('forms.and')} Privacy Policy before proceeding.");
      return;
    }
    setSubmitting(true);

    const nameParts = formData.name.trim().split(/\s+/);
    const fname = nameParts[0] || '';
    const lname = nameParts.slice(1).join(' ') || '.';

    const adults = formData.travelers.adults || 0;
    const children = formData.travelers.children || 0;
    const seniors = formData.travelers.seniors || 0;
    const infants = formData.travelers.infants || 0;
    const numberOfPax = adults + children + seniors + infants;

    const details = [];
    if (skipPassengerDetails) {
      for (let i = 0; i < adults; i++) details.push({ name: `Adult Guest ${i+1}`, ageType: 'adult', age: 30 });
      for (let i = 0; i < children; i++) details.push({ name: `Child Guest ${i+1}`, ageType: 'kid', age: 8 });
      for (let i = 0; i < seniors; i++) details.push({ name: `Senior Guest ${i+1}`, ageType: 'senior', age: 65 });
      for (let i = 0; i < infants; i++) details.push({ name: `Infant Guest ${i+1}`, ageType: 'infant', age: 1 });
    } else {
      passengerDetails.forEach(p => {
        details.push({
          name: p.name.trim(),
          ageType: p.ageType,
          age: p.ageType === 'adult' ? 30 : p.ageType === 'kid' ? 8 : p.ageType === 'senior' ? 65 : 1,
          passport: p.passport.trim()
        });
      });
    }

    const selectedExpNames = formData.selectedExperiences.map(id => {
      const found = experiences.find(e => e.id === id);
      return found ? found.name : '';
    }).filter(Boolean);

    let durationDays = 1;
    if (formData.arrivalDate && formData.departureDate) {
      const diffTime = Math.abs(new Date(formData.departureDate) - new Date(formData.arrivalDate));
      durationDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    }

    const itinerary = [];
    if (selectedExpNames.length > 0) {
      for (let i = 0; i < durationDays; i++) {
        itinerary.push({
          day: i + 1,
          experiences: []
        });
      }
      selectedExpNames.forEach((expName, idx) => {
        const dayIdx = idx % durationDays;
        itinerary[dayIdx].experiences.push(expName);
      });
    } else {
      for (let i = 0; i < durationDays; i++) {
        itinerary.push({
          day: i + 1,
          experiences: []
        });
      }
    }

    const payload = {
      customer: {
        title: formData.title,
        fname,
        lname,
        phone: `${formData.phoneCode} ${formData.phone}`,
        email: formData.email,
        country: formData.country,
        languages: formData.languages
      },
      pricing: {
        totalPrice: 0,
        depositPaid: 0,
        balanceDue: 0,
        currency: 'USD',
        tax: 0,
        discount: 0
      },
      travelers: {
        numberOfPax,
        details
      },
      status: 'pending',
      arrival: {
        date: formData.arrivalDate ? new Date(formData.arrivalDate) : undefined,
        time: formData.arrivalTime || undefined
      },
      departure: {
        date: formData.departureDate ? new Date(formData.departureDate) : undefined,
        time: formData.departureTime || undefined
      },
      specialNotes: formData.specialNotes,
      includes: [],
      excludes: [],
      tourTier: formData.tourTier,
      categories: formData.selectedCategories,
      itinerary
    };

    apiFetch('/bookings', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
      .then(res => {
        if (res && res.success && res.data) {
          setSuccessBooking(res.data);
        } else {
          throw new Error('Submit failed');
        }
      })
      .catch((err) => {
        alert(err.message || 'Unable to submit booking request. Please try again.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const steps = [
      { id: 1, title: t('forms.personal'), icon: 'fi-br-user' },
      { id: 2, title: t('forms.trip_info'), icon: 'fi-br-calendar' },
      { id: 3, title: t('forms.experiences'), icon: 'fi-br-map-marker' },
      { id: 4, title: t('forms.passengers'), icon: 'fi-br-id-card' },
      { id: 5, title: t('forms.confirm'), icon: 'fi-br-check' }
  ];

  if (loading) {
    return (
      <div className='flex flex-col w-full min-h-screen justify-center items-center pt-24 md:pt-32'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-KITH-primary'></div>
      </div>
    );
  }

  if (successBooking) {
    return (
      <div className='flex flex-col w-full min-h-screen pt-24 md:pt-32 pb-20 bg-gray-50/50 justify-center items-center px-4'>
        <SEO title="Custom Itinerary Requested" />
        <div className='max-w-2xl w-full bg-white rounded-[3rem] shadow-2xl p-8 md:p-12 border border-gray-100 text-center flex flex-col gap-6 items-center animate-fadeIn'>
          <div className='w-20 h-20 bg-green-50 rounded-full flex items-center justify-center text-green-500 shadow-md mb-2'>
            <i className='fi fi-br-check-circle text-4xl mt-1'></i>
          </div>
          <h2 className='text-3xl font-black text-gray-800 uppercase tracking-tight'>Request Received!</h2>
          <p className='text-gray-500 font-medium max-w-md'>
            Thank you, {formData.title} {formData.name.split(' ')[0]}! Your custom tour request has been received {t('forms.and')} is set to pending status.
          </p>

          <div className='w-full bg-gray-50 p-6 rounded-2xl border border-gray-100 flex flex-col gap-3 my-4 relative overflow-hidden'>
            <span className='text-[10px] font-black text-gray-400 uppercase tracking-widest'>Your Confirmation Number</span>
            <span className='text-3xl font-black text-KITH-primary tracking-widest'>{successBooking.confirmationNumber}</span>
            <p className='text-xs font-bold text-gray-500 mt-2'>
              <i className='fi fi-br-shield-check text-KITH-primary mr-2'></i>
              Do not share your Tour Number with anyone or any third party. You can use this number to securely track your tour from the <Link to={`/my-tour/${successBooking.confirmationNumber}`} className="text-KITH-primary underline">My Tour</Link> page.
            </p>
          </div>

          <div className='flex flex-col sm:flex-row gap-4 w-full justify-center'>
            <Link 
              to={`/my-tour/${successBooking.confirmationNumber}`}
              className='px-8 py-4 bg-KITH-primary text-white font-bold rounded-2xl hover:bg-KITH-primary-dark transition-all shadow-md text-sm whitespace-nowrap'
            >
              Track Your Tour
            </Link>
            <Link 
              to="/"
              className='px-8 py-4 bg-gray-100 text-gray-600 font-bold rounded-2xl hover:bg-gray-200 transition-all text-sm whitespace-nowrap'
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col w-full min-h-screen pt-24 md:pt-32 pb-20 bg-gray-50/30'>
      <SEO 
        title="Design Your Custom Tour" 
        description="Create your perfect Sri Lankan holiday by selecting your favorite experiences. KITH Travels will handle the rest." 
      />

      <div className='px-[5%] md:px-[10%] max-w-6xl mx-auto w-full'>
        
        {/* Page Header */}
        <div className='text-center mb-16 flex flex-col gap-4'>
            <h1 className='text-4xl md:text-5xl font-black text-gray-800 uppercase tracking-tight'>{t('forms.design_dream')}</h1>
            <p className='text-gray-500 font-medium max-w-xl mx-auto'>
                Craft a personalized itinerary by selecting your favorite destinations {t('forms.and')} experiences.
            </p>
        </div>

        {/* Step Progress Bar */}
        <div className='flex justify-between items-center mb-20 relative px-4'>
            <div className='absolute top-1/2 left-0 w-full h-0.5 bg-gray-200 -translate-y-1/2 z-0'></div>
            <div 
                className='absolute top-1/2 left-0 h-0.5 bg-KITH-primary -translate-y-1/2 z-0 transition-all duration-500'
                style={{ width: `${((activeStep - 1) / (steps.length - 1)) * 100}%` }}
            ></div>
            
            {steps.map((step) => (
                <div 
                    key={step.id}
                    onClick={() => handleStepClick(step.id)}
                    className='relative z-10 flex flex-col items-center gap-3 cursor-pointer'
                >
                    <div className={`w-10 h-10 md:w-14 md:h-14 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-lg border-2 ${activeStep >= step.id ? 'bg-KITH-primary border-KITH-primary text-white scale-110' : 'bg-white border-gray-100 text-gray-400 hover:border-KITH-primary/50'}`}>
                        <i className={`fi ${step.icon} text-sm md:text-lg`}></i>
                    </div>
                    <span className={`text-[8px] md:text-xs font-black uppercase tracking-widest hidden md:block transition-colors ${activeStep >= step.id ? 'text-KITH-primary' : 'text-gray-400'}`}>
                        {step.title}
                    </span>
                </div>
            ))}
        </div>

        <div className='bg-white rounded-[3.5rem] shadow-2xl p-8 md:p-14 border border-gray-100/50 relative'>
            <form onSubmit={(e) => e.preventDefault()} className='relative z-10'>
                
                {/* STEP 1: Personal Details */}
                <div className={`${activeStep === 1 ? 'flex' : 'hidden'} flex-col gap-10 animate-fadeIn`}>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-12'>
                        <div className='flex flex-col gap-3 group'>
                            <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t('forms.title')}</label>
                            <div className='flex gap-4'>
                                {['Mr', 'Mrs', 'Ms', 'Dr'].map(t => (
                                    <button 
                                        key={t} type="button"
                                        onClick={() => setFormData(prev => ({...prev, title: t}))}
                                        className={`px-4 py-2 rounded-xl font-bold text-sm border-2 transition-all ${formData.title === t ? 'bg-KITH-primary border-KITH-primary text-white shadow-md' : 'bg-gray-50 border-transparent text-gray-500 hover:bg-gray-100'}`}
                                    >{t}</button>
                                ))}
                            </div>
                        </div>
                        <div className='flex flex-col gap-3 group'>
                            <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t('forms.full_name')}</label>
                            <input 
                                type="text" name="name" required value={formData.name}
                                className='w-full bg-gray-50 px-5 py-4 rounded-2xl outline-none font-bold text-gray-700 shadow-inner'
                                placeholder="e.g. John Doe"
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className='flex flex-col gap-3 group'>
                            <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t('forms.email')}</label>
                            <input type="email" name="email" required value={formData.email} className='w-full bg-gray-50 px-5 py-4 rounded-2xl outline-none font-bold text-gray-700 shadow-inner' placeholder="john@example.com" onChange={handleInputChange} />
                        </div>
                        <div className='flex flex-col gap-3 group'>
                            <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t('forms.phone')}</label>
                            <div className='flex gap-2 w-full'>
                                <select 
                                    name="phoneCode" 
                                    value={formData.phoneCode} 
                                    onChange={handleInputChange}
                                    className='w-1/3 md:w-1/4 bg-gray-50 px-3 py-4 rounded-2xl outline-none font-bold text-gray-700 shadow-inner appearance-none cursor-pointer border-r-8 border-transparent'
                                >
                                    {popularCountryCodes.map(c => (
                                        <option key={c.code} value={c.code}>{c.code} {c.name}</option>
                                    ))}
                                </select>
                                <input type="tel" name="phone" required value={formData.phone} className='flex-1 bg-gray-50 px-5 py-4 rounded-2xl outline-none font-bold text-gray-700 shadow-inner' placeholder="234 567 890" onChange={handleInputChange} />
                            </div>
                        </div>
                        <div className='flex flex-col gap-3 group'>
                            <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t('forms.nationality')}</label>
                            <select 
                                name="country" 
                                value={formData.country} 
                                onChange={handleInputChange}
                                className='w-full bg-gray-50 px-5 py-4 rounded-2xl outline-none font-bold text-gray-700 shadow-inner appearance-none cursor-pointer border-r-8 border-transparent'
                            >
                                {popularCountries.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* STEP 2: Trip Info */}
                <div className={`${activeStep === 2 ? 'flex' : 'hidden'} flex-col gap-10 animate-fadeIn`}>
                    
                    {/* Tier Selection */}
                    <div className='bg-gray-50 p-8 rounded-[2.5rem] flex flex-col gap-6 border border-gray-100'>
                        <h4 className='font-black text-gray-800 uppercase tracking-widest text-xs flex items-center gap-3'>
                            <i className='fi fi-br-star text-KITH-primary'></i> Select Tour Option
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({...prev, tourTier: 'basic'}))}
                                className={`text-left p-6 rounded-3xl border-2 transition-all flex flex-col gap-2 ${formData.tourTier === 'basic' ? 'border-KITH-primary bg-white shadow-md' : 'border-transparent bg-gray-100/50 hover:bg-gray-100 text-gray-500'}`}
                            >
                                <span className={`font-black ${formData.tourTier === 'basic' ? 'text-gray-800' : ''}`}>Basic Tour</span>
                                <span className="text-xs">May have 3-4 Star Hotels</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({...prev, tourTier: 'premium'}))}
                                className={`text-left p-6 rounded-3xl border-2 transition-all flex flex-col gap-2 relative overflow-hidden ${formData.tourTier === 'premium' ? 'border-KITH-primary bg-KITH-primary/5 shadow-md' : 'border-transparent bg-gray-100/50 hover:bg-gray-100 text-gray-500'}`}
                            >
                                {formData.tourTier === 'premium' && <div className="absolute top-0 right-0 bg-KITH-primary text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-bl-xl">Upgraded</div>}
                                <span className={`font-black ${formData.tourTier === 'premium' ? 'text-gray-800' : ''}`}>Premium Tour</span>
                                <span className="text-xs">Only 5 Star Hotels or Luxury Resorts</span>
                            </button>
                        </div>
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
                        <div className='bg-gray-50 p-8 rounded-[2.5rem] flex flex-col gap-6 border border-gray-100'>
                            <h4 className='font-black text-gray-800 uppercase tracking-widest text-xs flex items-center gap-3'>
                                <i className='fi fi-br-plane-arrival text-KITH-primary'></i> {t('forms.arrival_details')}</h4>
                            <div className='flex flex-col gap-4'>
                                <input type="date" name="arrivalDate" min={getMinArrivalDate()} value={formData.arrivalDate} className='w-full bg-white px-4 py-3 rounded-xl font-bold text-gray-700 shadow-sm border border-transparent focus:border-KITH-primary outline-none' onChange={handleInputChange} />
                                <input type="time" name="arrivalTime" value={formData.arrivalTime} className='w-full bg-white px-4 py-3 rounded-xl font-bold text-gray-700 shadow-sm border border-transparent focus:border-KITH-primary outline-none' onChange={handleInputChange} />
                            </div>
                        </div>
                        <div className='bg-gray-50 p-8 rounded-[2.5rem] flex flex-col gap-6 border border-gray-100'>
                            <h4 className='font-black text-gray-800 uppercase tracking-widest text-xs flex items-center gap-3'>
                                <i className='fi fi-br-plane-departure text-KITH-primary'></i> {t('forms.departure_details')}</h4>
                            <div className='flex flex-col gap-4'>
                                <input type="date" name="departureDate" min={getMinDepartureDate()} value={formData.departureDate} className='w-full bg-white px-4 py-3 rounded-xl font-bold text-gray-700 shadow-sm border border-transparent focus:border-KITH-primary outline-none' onChange={handleInputChange} />
                                <input type="time" name="departureTime" value={formData.departureTime} className='w-full bg-white px-4 py-3 rounded-xl font-bold text-gray-700 shadow-sm border border-transparent focus:border-KITH-primary outline-none' onChange={handleInputChange} />
                            </div>
                        </div>
                        <div className='md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4 mt-4'>
                             {Object.entries(formData.travelers).map(([type, count]) => (
                                 <div key={type} className='bg-gray-50 p-4 rounded-2xl flex flex-col items-center gap-3 border border-gray-100'>
                                     <span className='text-[10px] font-black text-gray-400 uppercase tracking-tighter'>{type}</span>
                                     <div className='flex items-center gap-3'>
                                         <button type="button" onClick={() => updateTraveler(type, -1)} className='w-6 h-6 rounded bg-KITH-primary text-white font-black'>-</button>
                                         <span className='font-black text-gray-700'>{count}</span>
                                         <button type="button" onClick={() => updateTraveler(type, 1)} className='w-6 h-6 rounded bg-KITH-primary text-white font-black'>+</button>
                                     </div>
                                 </div>
                             ))}
                        </div>
                    </div>
                </div>

                {/* STEP 3: Experiences Selection */}
                <div className={`${activeStep === 3 ? 'flex' : 'hidden'} flex-col gap-10 animate-fadeIn`}>
                    <div className='flex flex-col gap-6'>
                        <span className='font-black text-gray-800 uppercase tracking-widest text-xs'>{t('forms.select_categories')}</span>
                        <div className='flex flex-wrap gap-3'>
                            {categoriesList.map(cat => (
                                <button 
                                    key={cat} type="button"
                                    onClick={() => toggleCategory(cat)}
                                    className={`px-4 py-2 rounded-xl font-bold text-[10px] md:text-xs uppercase tracking-wider border-2 transition-all ${formData.selectedCategories.includes(cat) ? 'bg-KITH-primary border-KITH-primary text-white shadow-md' : 'bg-white border-gray-100 text-gray-500 hover:border-KITH-primary/30'}`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className='flex justify-between items-center bg-gray-50 p-6 rounded-3xl mt-4'>
                        <span className='font-black text-gray-800 uppercase tracking-widest text-xs'>{t('forms.choose_favorites')}</span>
                        <span className='bg-KITH-primary text-white px-4 py-1 rounded-full text-[10px] font-black'>{formData.selectedExperiences.length} Selected</span>
                    </div>

                    {/* Show Selected Experiences Chips */}
                    {formData.selectedExperiences.length > 0 && (
                        <div className="flex flex-wrap gap-2 px-2">
                            {formData.selectedExperiences.map(id => {
                                const exp = experiences.find(e => e.id === id);
                                return exp ? (
                                    <div key={id} className="flex items-center gap-2 bg-gray-100 border border-gray-200 px-3 py-1.5 rounded-full text-[10px] font-bold text-gray-700">
                                        <span>{exp.name}</span>
                                        <button type="button" onClick={() => toggleExperience(id)} className="text-gray-400 hover:text-red-500 transition-colors">
                                            <i className="fi fi-br-cross text-[8px]"></i>
                                        </button>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    )}

                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-h-[500px] overflow-y-auto pr-4 pb-4 scrollbarThin overscroll-contain'>
                        {experiences.length > 0 ? (
                            experiences.map(exp => (
                                <div 
                                    key={exp.id}
                                    onClick={() => toggleExperience(exp.id)}
                                    className={`relative w-full h-40 sm:h-48 md:h-56 rounded-[2rem] overflow-hidden cursor-pointer group border-4 transition-all duration-300 ${formData.selectedExperiences.includes(exp.id) ? 'border-KITH-primary scale-[0.98]' : 'border-transparent shadow-md'}`}
                                >
                                    <div className='w-full h-full'>
                                        <DynamicImage HighImage={exp.image.h} LowImage={exp.image.l} />
                                    </div>
                                    <div className='absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center'>
                                         <i className={`fi ${formData.selectedExperiences.includes(exp.id) ? 'fi-br-trash' : 'fi-br-plus'} text-white text-2xl`}></i>
                                    </div>
                                    <div className='absolute bottom-0 left-0 p-4 w-full bg-gradient-to-t from-black/80 to-transparent pointer-events-none'>
                                        <h5 className='text-white font-bold text-xs truncate'>{exp.name}</h5>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className='col-span-full py-20 text-center text-gray-500 font-bold'>
                                No experiences available...
                            </div>
                        )}
                    </div>
                </div>

                {/* STEP 4: Passenger Details */}
                <div className={`${activeStep === 4 ? 'flex' : 'hidden'} flex-col gap-10 animate-fadeIn`}>
                    <div className="flex justify-between items-center">
                        <h2 className='text-xl font-black text-gray-800 uppercase tracking-tight'>{t('forms.traveler_details')}</h2>
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input 
                                type="checkbox" 
                                checked={skipPassengerDetails} 
                                onChange={(e) => setSkipPassengerDetails(e.target.checked)} 
                                className="rounded border-gray-300 text-KITH-primary focus:ring-KITH-primary w-4 h-4"
                            />
                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{t('forms.skip_details')}</span>
                        </label>
                    </div>
                    
                    {!skipPassengerDetails ? (
                        <div className="flex flex-col gap-6 max-h-[400px] overflow-y-auto pr-2 scrollbarThin">
                            {passengerDetails.map((p, idx) => (
                                <div key={idx} className="p-6 bg-gray-50 rounded-3xl border border-gray-100 flex flex-col gap-4">
                                    <div className="flex justify-between items-center border-b border-gray-200/60 pb-2">
                                        <span className="text-xs font-black text-KITH-primary uppercase tracking-widest">{p.label}</span>
                                        <span className="text-[10px] font-bold bg-white text-gray-500 px-3 py-1 rounded-full border border-gray-100 uppercase">
                                            {t('forms.category')} {p.ageType === 'adult' ? 'Adult' : p.ageType === 'kid' ? 'Child' : p.ageType === 'senior' ? 'Senior' : 'Infant'}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex flex-col gap-2">
                                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">{t('forms.full_name')}</label>
                                            <input 
                                                type="text" 
                                                value={p.name}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setPassengerDetails(prev => prev.map((item, i) => i === idx ? { ...item, name: val } : item));
                                                }}
                                                placeholder="As in Passport"
                                                className="w-full bg-white px-4 py-3 rounded-xl border border-transparent focus:border-KITH-primary outline-none font-bold text-gray-700 text-sm shadow-sm"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">{t('forms.passport_number')}</label>
                                            <input 
                                                type="text" 
                                                value={p.passport}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setPassengerDetails(prev => prev.map((item, i) => i === idx ? { ...item, passport: val } : item));
                                                }}
                                                placeholder="Passport Number"
                                                className="w-full bg-white px-4 py-3 rounded-xl border border-transparent focus:border-KITH-primary outline-none font-bold text-gray-700 text-sm shadow-sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-8 bg-gray-50 rounded-3xl border border-dashed border-gray-200 text-center flex flex-col items-center gap-3">
                            <i className="fi fi-br-shield-check text-3xl text-gray-400"></i>
                            <p className="text-sm font-bold text-gray-500">{t('forms.skip_msg_1')}</p>
                            <p className="text-xs text-gray-400">{t('forms.skip_msg_2')}</p>
                        </div>
                    )}
                </div>

                {/* STEP 5: Finalize */}
                <div className={`${activeStep === 5 ? 'flex' : 'hidden'} flex-col gap-10 animate-fadeIn`}>
                    <div className='flex flex-col gap-4'>
                        <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t('forms.pref_languages')}</label>
                        <div className='flex flex-wrap gap-4'>
                             {['English', 'German', 'French', 'Chinese', 'Spanish'].map(lang => (
                                <button 
                                    key={lang} type="button"
                                    onClick={() => handleLanguageChange(lang)}
                                    className={`px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 transition-all ${formData.languages.includes(lang) ? 'bg-KITH-primary border-KITH-primary text-white shadow-md' : 'bg-gray-50 border-transparent text-gray-500 hover:bg-gray-100'}`}
                                >{lang}</button>
                             ))}
                        </div>
                    </div>
                    <div className='flex flex-col gap-4'>
                        <label className='text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1'>{t('forms.additional_preferences')}</label>
                        <textarea 
                            name="specialNotes" value={formData.specialNotes}
                            className='w-full min-h-[180px] bg-gray-50 border-transparent focus:border-KITH-primary outline-none p-8 rounded-[2.5rem] transition-all font-bold text-gray-700 shadow-inner resize-none'
                            placeholder={t('forms.additional_placeholder')}
                            onChange={handleInputChange}
                        ></textarea>
                    </div>
                    <div className='flex flex-col gap-4 mt-6'>
                        <label className="flex items-start gap-3 cursor-pointer select-none bg-KITH-primary/5 p-4 rounded-2xl border border-KITH-primary/20">
                            <input 
                                type="checkbox" 
                                checked={agreedToTerms} 
                                onChange={(e) => setAgreedToTerms(e.target.checked)} 
                                className="mt-1 rounded border-gray-300 text-KITH-primary focus:ring-KITH-primary w-5 h-5"
                                required
                            />
                            <span className="text-sm text-gray-700 leading-relaxed font-medium">
                                I have read {t('forms.and')} agree to the <Link to="/terms-and-conditions" target="_blank" className="text-KITH-primary hover:underline font-bold">{t('forms.terms_use')}</Link> {t('forms.and')} <Link to="/privacy-policy" target="_blank" className="text-KITH-primary hover:underline font-bold">{t('forms.privacy_policy')}</Link>.
                            </span>
                        </label>
                    </div>
                </div>

                {/* Navigation */}
                <div className='flex justify-between items-center mt-16 pt-10 border-t border-gray-100'>
                    <button 
                        type="button" onClick={() => setActiveStep(prev => Math.max(1, prev - 1))}
                        className={`px-8 py-3 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all ${activeStep === 1 ? 'opacity-0' : 'text-gray-400 hover:text-gray-800'}`}
                    >{t('forms.back')}</button>
                    
                    {activeStep < 5 ? (
                        <button 
                            type="button" onClick={handleNextStep}
                            className='px-10 py-4 bg-KITH-primary text-white font-black uppercase tracking-widest text-[10px] rounded-xl shadow-xl hover:bg-KITH-primary-dark transition-all'
                        >{t('forms.next_step')}</button>
                    ) : (
                        <button type="button" onClick={handleSubmit} disabled={submitting} className='px-12 py-5 bg-KITH-primary text-white font-black uppercase tracking-widest text-xs rounded-2xl shadow-2xl hover:bg-KITH-primary-dark transition-all disabled:bg-gray-300 disabled:cursor-not-allowed'>
                            {submitting ? t('forms.requesting') : t('forms.request_custom')}
                        </button>
                    )}
                </div>

            </form>
        </div>
      </div>
    </div>
  )
}

export default CustomTour
